import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  toast,
} from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { useRef, useState } from "react";
import type { VisitorRedeemLookupDTO } from "../api/client";
import { visitorLotteryApi } from "../api/client";
import { useRedeemByCodeMutation } from "../api/queries";
import {
  VisitorQrCameraScanner,
  type VisitorQrCameraScannerHandle,
} from "../ui/VisitorQrCameraScanner";

export function VisitorLotteryScannerPage() {
  const nav = useNavigate();
  const canWrite = useActionPermission(["visitor-lottery:write"]);
  const [code, setCode] = useState("");
  const [lookup, setLookup] = useState<VisitorRedeemLookupDTO | null>(null);
  const [looking, setLooking] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const scannerRef = useRef<VisitorQrCameraScannerHandle>(null);
  const redeemMutation = useRedeemByCodeMutation();

  const handleLookup = async (raw?: string) => {
    const value = (raw ?? code).trim().toUpperCase();
    if (!value) return;
    setLooking(true);
    setLookup(null);
    try {
      const row = await visitorLotteryApi.lookupRedeemCode(value);
      setLookup(row);
      setCode(value);
      toast.success("ພົບໂຄດແລ້ວ");
    } catch {
      toast.error("ບໍ່ພົບໂຄດນີ້");
      setLookup(null);
    } finally {
      setLooking(false);
      inputRef.current?.select();
    }
  };

  const handleRedeem = async () => {
    if (!lookup?.redeemCode || !canWrite) return;
    try {
      const result = await redeemMutation.mutateAsync(lookup.redeemCode);
      setLookup(result.registration);
      toast.success(
        result.alreadyRedeemed
          ? "ໂຄດນີ້ຮັບຂອງໄປແລ້ວ"
          : "ໝາຍວ່າຮັບຂອງແລ້ວ",
      );
    } catch {
      toast.error("ບໍ່ສາມາດ redeem ໄດ້");
    }
  };

  const goToRegistrations = async () => {
    await scannerRef.current?.stop();
    void nav({ to: "/app/visitor-lottery/registrations" });
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "Visitor Lottery", href: "/app/visitor-lottery" },
            { label: "Scanner" },
          ]}
        />
      </Header>
      <Main>
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold">ສະແກນ / ຮັບຂອງ</h1>
            <p className="text-muted-foreground text-sm">
              ເປີດກ້ອງສະແກນ QR ຫຼື ພິມໂຄດຮັບຂອງ ແລ້ວກົດ Redeem
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => void goToRegistrations()}
          >
            ເບິ່ງລາຍຊື່
          </Button>
        </div>

        <Card className="mx-auto max-w-xl">
          <CardHeader>
            <CardTitle>ໂຄດຮັບຂອງ</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <VisitorQrCameraScanner
              ref={scannerRef}
              disabled={looking}
              onScan={(scanned) => {
                setCode(scanned);
                void handleLookup(scanned);
              }}
            />

            <div className="space-y-2">
              <Label htmlFor="redeem-code">Redeem code</Label>
              <Input
                ref={inputRef}
                id="redeem-code"
                autoFocus
                className="font-mono text-lg tracking-wider uppercase"
                placeholder="FL-V-XXXXXX"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    void handleLookup();
                  }
                }}
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => void handleLookup()}
                disabled={looking || !code.trim()}
              >
                Lookup
              </Button>
              {canWrite ? (
                <Button
                  variant="secondary"
                  disabled={
                    !lookup?.redeemCode ||
                    Boolean(lookup.redeemedAt) ||
                    redeemMutation.isPending
                  }
                  onClick={() => void handleRedeem()}
                >
                  Mark redeemed
                </Button>
              ) : null}
            </div>

            {lookup ? (
              <div className="space-y-2 rounded-lg border p-4">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-medium">{lookup.fullName}</p>
                  {lookup.redeemedAt ? (
                    <Badge variant="default">ຮັບແລ້ວ</Badge>
                  ) : (
                    <Badge variant="secondary">ລໍຖ້າຮັບ</Badge>
                  )}
                </div>
                <p className="text-muted-foreground text-sm">
                  {lookup.nickname ? `(${lookup.nickname}) · ` : null}
                  {lookup.phoneNumber}
                </p>
                <p className="text-sm">
                  ລາງວັນ: <strong>{lookup.prizeName ?? "—"}</strong>
                </p>
                <p className="font-mono text-lg tracking-widest">
                  {lookup.redeemCode}
                </p>
                <p className="text-muted-foreground text-xs">
                  {lookup.eventTitle}
                </p>
              </div>
            ) : null}
          </CardContent>
        </Card>
      </Main>
    </>
  );
}
